import PropTypes from 'prop-types';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  onMembershipNotAccepted,
  onTermsRequired,
  onUnauthorized,
  setOrganizationId as setOrgId,
  verifyCaptcha,
} from '@/lib';
import {
  apiErrorName,
  loginRequest,
  logoutRequest,
  validateTokenRequest,
} from '@/lib/recepthor-api';

import { LAYOUT_CAPABLE, homeForCapability } from './constants.js';
import queryClient from './queryClient.js';
import { clearDatadogUser, setDatadogUser } from './datadog.js';
import { hardNavigate } from './hardNavigate.js';

const AuthContext = createContext();

// Upper bound for the validate-token request (REC-1955). /login and every
// protected route wait on `loading` for their first check, so a request that
// never answers kept them on the spinner forever. Past this time the request
// is aborted and handled as a network error: local auth state stays and
// `loading` drops. 10 s, the same bound as the terms catalog read of the same
// login flow (TERMS_CATALOG_TIMEOUT_MS): far above a normal answer, short
// enough that the user gets the screen back.
export const VALIDATE_TOKEN_TIMEOUT_MS = 10_000;

function hasLayoutCapability(membership) {
  return LAYOUT_CAPABLE.has(membership.capabilities?.[0]);
}

export function resolveActiveOrg(memberships, savedOrgId, defaultOrgId) {
  if (!Array.isArray(memberships) || memberships.length === 0) return null;
  // 1. Try saved selection from localStorage
  if (savedOrgId) {
    const saved = memberships.find((m) => m.organizationId === savedOrgId);
    if (saved) return saved;
  }
  // 2. First layout-capable org where user is owner, else the first
  //    layout-capable org, else the first org
  const pick =
    memberships.find((m) => m.isOwner && hasLayoutCapability(m)) ??
    memberships.find(hasLayoutCapability) ??
    memberships[0];
  // 3. The API's default org replaces that pick only to take an invited user
  //    out of their empty personal org and into the studio. Any other pick
  //    (backoffice, receptor, another studio) stays.
  if (defaultOrgId && pick.capabilities?.[0] === 'solo_law_firm') {
    const preferred = memberships.find(
      (m) =>
        m.organizationId === defaultOrgId &&
        m.capabilities?.[0] === 'enterprise_law_firm',
    );
    if (preferred) return preferred;
  }
  return pick;
}

export function resolveCapability(org) {
  if (!org) return null;
  return org.capabilities?.[0] ?? null;
}

// Where the hard reload of an org switch lands: the entry of the chosen org's
// capability, not '/'. '/' is the studio dashboard, and ProtectedRoute moves a
// session whose active capability does not fit the route to a membership that
// does (REC-1935), so landing a Plataforma or receptor switch on '/' sent the
// user straight back to their studio.
export function switchDestination(memberships, orgId) {
  const org = (memberships ?? []).find((m) => m.organizationId === orgId);
  return homeForCapability(resolveCapability(org)) ?? '/';
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(
    () => localStorage.getItem('selectedOrgId') ?? null,
  );
  // defaultOrganizationId from the last login / validate-token response.
  const [defaultOrgId, setDefaultOrgId] = useState(null);
  // True only after an authenticated API call returns 401 (the session expired
  // or was invalidated while in use). Drives the "session expired" notice on the
  // login screen; an in-session 401 is the only thing that sets it, so first
  // visits and refreshes while logged out never trigger the notice.
  const [sessionExpired, setSessionExpired] = useState(false);
  // True when the API refused a session, or a login, because the user never
  // accepted the terms in force. The activation email is the only path that
  // records the acceptance, so the login screen offers to send it.
  const [termsPending, setTermsPending] = useState(false);
  // True when an in-session request was refused because the active
  // membership is still INVITED. The session is valid, and the member has to
  // accept or reject the invitation before acting under it.
  const [membershipPending, setMembershipPending] = useState(false);
  // validate-token calls in flight. Unmount aborts them and marks them
  // cancelled, so a late answer neither updates state nor logs.
  const pendingValidations = useRef(new Set());

  function applyAuthData(data) {
    setSessionExpired(false);
    setTermsPending(false);
    setMembershipPending(false);
    const mships = data.memberships ?? [];
    const rawUser = data.user ?? data;
    setIsAuthenticated(true);
    setUser(rawUser);
    setDatadogUser(rawUser);
    setMemberships(mships);
    const defaultId = data.defaultOrganizationId ?? null;
    setDefaultOrgId(defaultId);
    // Eagerly sync orgContext so API calls made before the next render
    // already have the X-Organization-Id header set.
    const org = resolveActiveOrg(mships, selectedOrgId, defaultId);
    // A refresh can move the active org mid-session (e.g. after accepting a
    // studio invitation). Cached queries belong to the previous org, so drop
    // them as selectOrganization does. Not needed on the first login.
    if (isAuthenticated && org?.organizationId !== organizationId) {
      queryClient.removeQueries();
    }
    setOrgId(org?.organizationId ?? null);
  }

  function clearAuth() {
    queryClient.clear();
    setIsAuthenticated(false);
    setUser(null);
    clearDatadogUser();
    setMemberships([]);
    setSelectedOrgId(null);
    setDefaultOrgId(null);
    localStorage.removeItem('selectedOrgId');
    setOrgId(null);
  }

  // Derive active org and capability from state
  const activeOrg = useMemo(
    () => resolveActiveOrg(memberships, selectedOrgId, defaultOrgId),
    [memberships, selectedOrgId, defaultOrgId],
  );
  const organizationId = activeOrg?.organizationId ?? null;
  const organizationName = activeOrg?.organizationName ?? '';
  const activeCapability = resolveCapability(activeOrg);
  const permissions = activeOrg?.permissions ?? [];
  const isOwner = activeOrg?.isOwner ?? false;
  // User-level "internal" flag: true when any of the user's memberships is the internal
  // (backoffice) org type. Deliberately NOT the active org, so an internal user keeps
  // internal-only affordances even while acting under another profile.
  const isInternal = memberships.some(
    (m) => resolveCapability(m) === 'backoffice',
  );

  // Sync orgContext module synchronously during render so that children
  // read the correct organizationId on their very first fetch.
  // A useEffect would run AFTER children mount, causing a race condition
  // where tables fire API calls before the org header is set.
  setOrgId(organizationId);

  function selectOrganization(orgId) {
    setSelectedOrgId(orgId);
    localStorage.setItem('selectedOrgId', orgId);
    setOrgId(orgId);
    // Org data lives behind the X-Organization-Id header, which React Query
    // doesn't know is part of cache identity. Drop every cached query so each
    // table refetches under the new org instead of showing the previous org's
    // data. setOrgId already ran, so the refetch on remount carries the new
    // header. Mirrors clearAuth's queryClient.clear() on logout.
    queryClient.removeQueries();
  }

  // User-initiated org switch (header / user menu). Persist the choice, then HARD
  // RELOAD to the chosen org's entry (switchDestination). A soft SPA switch left
  // stale cross-org state in non-React-Query surfaces (e.g. a deep-linked by-id
  // view rendered from another org's router state): the view wouldn't refetch,
  // and an F5 then half-rendered a foreign task. A full reload guarantees EVERY surface re-fetches under the new org from
  // a clean slate. localStorage is set first so AuthContext resolves the new org
  // on boot (selectedOrgId initialises from it). Keep selectOrganization (above)
  // as the pure setter for login / first-org selection, where a reload is wrong.
  function switchOrganization(orgId) {
    if (!orgId || orgId === organizationId) return;
    localStorage.setItem('selectedOrgId', orgId);
    setOrgId(orgId);
    hardNavigate(switchDestination(memberships, orgId));
  }

  const validateToken = async () => {
    const call = { controller: new AbortController(), cancelled: false };
    // AbortController and not AbortSignal.timeout: the timer has to be cleared
    // when the call settles and when the provider unmounts.
    call.timer = setTimeout(() => {
      call.controller.abort(
        new DOMException(
          `validate-token did not answer within ${VALIDATE_TOKEN_TIMEOUT_MS} ms`,
          'TimeoutError',
        ),
      );
    }, VALIDATE_TOKEN_TIMEOUT_MS);
    pendingValidations.current.add(call);
    try {
      const response = await validateTokenRequest({
        signal: call.controller.signal,
      });
      if (call.cancelled) return null;

      if (response.ok) {
        const data = await response.json();
        if (call.cancelled) return null;
        // validate-token always returns 200; `authenticated` reports the
        // session state. False (or any non-true) means no session.
        if (data.authenticated) {
          applyAuthData(data);
          return { membershipCount: (data.memberships ?? []).length };
        } else {
          clearAuth();
          setTermsPending(data.reason === 'TermsNotAccepted');
          return { membershipCount: 0 };
        }
      } else {
        // 5xx / unexpected — we cannot confirm whether the session is valid,
        // so don't wipe local auth. The user can retry or log in manually.
        // statusText is empty over HTTP/2, so log the status too.
        console.warn(
          'Error validating token:',
          response.status,
          response.statusText,
        );
      }
    } catch (error) {
      // Network error / abort — same reasoning as 5xx: keep auth state intact.
      // The timeout abort lands here too and logs as a network failure.
      if (!call.cancelled) {
        console.error('Network error during token validation:', error);
      }
    } finally {
      clearTimeout(call.timer);
      pendingValidations.current.delete(call);
      if (!call.cancelled) setLoading(false);
    }
    return null;
  };

  useEffect(() => {
    const pending = pendingValidations.current;
    validateToken();
    return () => {
      for (const call of pending) {
        call.cancelled = true;
        clearTimeout(call.timer);
        call.controller.abort();
      }
      pending.clear();
    };
  }, []);

  // React to a 401 raised by an authenticated API call: flag the session as
  // expired and clear local auth. Ignored when there is no active session, so
  // 401s from public endpoints do not surface the notice.
  useEffect(() => {
    return onUnauthorized(() => {
      if (!isAuthenticated) return;
      setSessionExpired(true);
      clearAuth();
    });
  }, [isAuthenticated]);

  // A 403 TermsNotAccepted during the session: every organization-scoped call
  // is refused from here on, so the session is over and the login offers the
  // activation email that records the acceptance.
  useEffect(() => {
    return onTermsRequired(() => {
      if (!isAuthenticated) return;
      setTermsPending(true);
      clearAuth();
    });
  }, [isAuthenticated]);

  // A 403 MembershipNotAccepted ends the session as TermsNotAccepted does, and
  // login asks the member to resolve the invitation first.
  useEffect(() => {
    return onMembershipNotAccepted(() => {
      if (!isAuthenticated) return;
      setMembershipPending(true);
      clearAuth();
    });
  }, [isAuthenticated]);

  const login = async (email, password, captchaToken) => {
    setLoading(true);
    // Status de la respuesta HTTP que originó el throw, si hubo una; null
    // cuando la falla no viene de una respuesta (red, captcha, parseo).
    let httpStatus = null;
    try {
      await verifyCaptcha(captchaToken);

      const response = await loginRequest({ email, password, captchaToken });

      if (response.ok) {
        const data = await response.json();
        applyAuthData(data);
        return { success: true, memberships: data.memberships };
      } else {
        const status = response.status;
        httpStatus = status;
        const body = await response.json().catch(() => null);
        const errorName = apiErrorName(body);
        let errorMessage = 'Error desconocido al iniciar sesión.';

        // Both refusals are resolved through the activation email, and the
        // login screen reads the code to offer it.
        if (status === 403 && errorName === 'TermsNotAccepted') {
          const error = new Error(
            'Debes aceptar los términos y condiciones vigentes para ingresar.',
          );
          error.code = 'TermsNotAccepted';
          throw error;
        }
        if (status === 403 && errorName === 'UserNotActive') {
          const error = new Error('Tu cuenta aún no está activa.');
          error.code = 'UserNotActive';
          throw error;
        }

        if (status === 400 && errorName === 'CaptchaInvalid') {
          // El token de Turnstile es de un solo uso; si llega usado/vencido el
          // backend responde CaptchaInvalid. El form ya lo resetea tras cada
          // intento — este mensaje cubre el caso residual (token expirado).
          errorMessage =
            'La verificación de seguridad expiró. Inténtalo de nuevo.';
        } else if (status === 400) {
          errorMessage = 'Solicitud incorrecta. Verifica tus datos.';
        } else if (status === 401) {
          errorMessage = 'No autorizado. Credenciales incorrectas.';
        } else if (status === 403) {
          errorMessage = 'Acceso prohibido.';
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      if (httpStatus === null) {
        console.error('Network error during login:', error);
      } else if (![400, 401, 403].includes(httpStatus)) {
        console.error(`Unexpected HTTP ${httpStatus} during login:`, error);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      const response = await logoutRequest();

      if (response.ok) {
        clearAuth();
      } else {
        console.error('Error during logout:', response.statusText);
      }
    } catch (error) {
      console.error('Network error during logout:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    return validateToken();
  };

  return (
    <AuthContext.Provider
      value={{
        activeCapability,
        defaultOrganizationId: defaultOrgId,
        isOwner,
        isAuthenticated,
        isInternal,
        loading,
        login,
        logout,
        memberships,
        organizationId,
        organizationName,
        permissions,
        refreshUser,
        selectOrganization,
        sessionExpired,
        switchOrganization,
        termsPending,
        membershipPending,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
